import { Injectable } from '@angular/core';
import { PatientStore } from '@modules/patient/state/patient-store';
import { RxModel } from '@shared/models/rx-models/interfaces/rx-model';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { Observable, combineLatest, merge } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import { PatientAppIframeCommunicationService } from './patient-app-iframe-communication.service';

@Injectable({
	providedIn: 'root'
})
export class PatientAppInitService {
	/**
	 * Shared stream that loads patient from Rx and registers iframe events.
	 * Uses shareReplay to prevent duplicate iframe listeners when subscribed
	 * from multiple places (e.g. header facade + PatientComponent).
	 */
	readonly initializePatientAppStreams$: Observable<void | MessageEvent<any>> = merge(
		this.registerToPatientAppEvents$(),
		this.loadPatientFromRx$()
	).pipe(shareReplay({ bufferSize: 1, refCount: true }));

	constructor(
		private readonly patientStore: PatientStore,
		private readonly shellQuery: ShellQuery,
		private readonly patientAppIframeCommunicationService: PatientAppIframeCommunicationService
	) {}

	private registerToPatientAppEvents$(): Observable<MessageEvent> {
		return this.patientAppIframeCommunicationService.registerToPatientAppEvents();
	}

	private loadPatientFromRx$(): Observable<void> {
		return combineLatest({
			rx: this.shellQuery.rx$,
			clonedRx: this.shellQuery.clonedRx$,
			regulatoryDOBMask: this.shellQuery.regulatoryDOBMask$
		}).pipe(
			filter(
				(latest): latest is { rx: RxModel; clonedRx: RxModel; regulatoryDOBMask: string | undefined } =>
					!!latest.rx || !!latest.clonedRx
			),
			map(({ rx, clonedRx, regulatoryDOBMask }) => {
				const loadedRx = rx || clonedRx;

				this.patientStore.updatePatient(loadedRx.Patient, null, regulatoryDOBMask);
			})
		);
	}
}
